import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { PlatformAdminAuthProvider } from './contexts/PlatformAdminAuthContext'
import { TenantProvider } from './contexts/TenantContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { EmbedProvider } from './contexts/EmbedContext'
import { CartProvider } from './contexts/CartContext'
import { LocationFilterProvider } from './contexts/LocationFilterContext'
import { NotificationContainer } from './components/Notification'
import ErrorBoundary from './components/ErrorBoundary'
import ProtectedRoute from './components/ProtectedRoute'
import Layout from './components/Layout'
import { Spinner } from './components/Spinner'

// Lazy load pages for better code splitting and performance
// Login pages are loaded immediately for faster initial load
import Login from './pages/Login'
import CustomerLogin from './pages/CustomerLogin'
import CustomerSignup from './pages/CustomerSignup'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'

// Admin/Staff pages - lazy loaded
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Members = lazy(() => import('./pages/Members'))
const CheckIn = lazy(() => import('./pages/CheckIn'))
const POS = lazy(() => import('./pages/POS'))
const Bookings = lazy(() => import('./pages/Bookings'))
const Inventory = lazy(() => import('./pages/Inventory'))
const Reports = lazy(() => import('./pages/Reports'))
const Waiver = lazy(() => import('./pages/Waiver'))
const Admin = lazy(() => import('./pages/Admin'))
const YouthPrograms = lazy(() => import('./pages/YouthPrograms'))
const TimeClock = lazy(() => import('./pages/TimeClock'))
const Integrations = lazy(() => import('./pages/Integrations'))
const RoutesPage = lazy(() => import('./pages/RoutesPage'))
const Households = lazy(() => import('./pages/Households'))
const Competitions = lazy(() => import('./pages/Competitions'))
const Leads = lazy(() => import('./pages/Leads'))
const Incidents = lazy(() => import('./pages/Incidents'))
const PrivateEvents = lazy(() => import('./pages/PrivateEvents'))
const Analytics = lazy(() => import('./pages/Analytics'))

// Customer portal pages - lazy loaded
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'))
const CustomerClasses = lazy(() => import('./pages/CustomerClasses'))
const CustomerProfile = lazy(() => import('./pages/CustomerProfile'))
const CustomerPasses = lazy(() => import('./pages/CustomerPasses'))
const CustomerGiftCards = lazy(() => import('./pages/CustomerGiftCards'))
const CustomerRoutes = lazy(() => import('./pages/CustomerRoutes'))
const CustomerSends = lazy(() => import('./pages/CustomerSends'))

// Embed portal pages - lazy loaded
const EmbedPortal = lazy(() => import('./pages/embed/EmbedPortal'))

// Purchase Orders page
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'))

// Storefront embed pages (public, no auth)
const Storefront = lazy(() => import('./pages/embed/Storefront'))
const ShoppingCart = lazy(() => import('./pages/embed/ShoppingCart'))
const GuestCheckout = lazy(() => import('./pages/embed/GuestCheckout'))
const CheckoutSuccess = lazy(() => import('./pages/embed/CheckoutSuccess'))
const DayPassPurchase = lazy(() => import('./pages/DayPassPurchase'))

// Platform admin pages - lazy loaded
const PlatformAdminRoutes = lazy(() => import('./components/PlatformAdminRoutes'))

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <NotificationProvider>
        <TenantProvider>
          <AuthProvider>
            <BrowserRouter>
              <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Spinner size="lg" /></div>}>
                <Routes>
                  {/* Admin/Staff Routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/" element={
                    <ProtectedRoute>
                      <LocationFilterProvider>
                        <Layout />
                      </LocationFilterProvider>
                    </ProtectedRoute>
                  }>
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="members" element={<Members />} />
                    <Route path="check-in" element={<CheckIn />} />
                    <Route path="pos" element={<POS />} />
                    <Route path="bookings" element={<Bookings />} />
                    <Route path="inventory" element={<Inventory />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="waiver" element={<Waiver />} />
                    <Route path="admin" element={<Admin />} />
                    <Route path="youth-programs" element={<YouthPrograms />} />
                    <Route path="routes" element={<RoutesPage />} />
                    <Route path="households" element={<Households />} />
                    <Route path="competitions" element={<Competitions />} />
                    <Route path="leads" element={<Leads />} />
                    <Route path="incidents" element={<Incidents />} />
                    <Route path="private-events" element={<PrivateEvents />} />
                    <Route path="analytics" element={<Analytics />} />
                    <Route path="integrations" element={<Integrations />} />
                    <Route path="purchase-orders" element={<PurchaseOrders />} />
                  </Route>
                  {/* Time Clock kiosk — public, no auth required */}
                  <Route path="/timeclock" element={<TimeClock />} />

                  {/* Customer Portal Routes */}
                  <Route path="/customer/login" element={<CustomerLogin />} />
                  <Route path="/customer/signup" element={<CustomerSignup />} />
                  <Route path="/customer/forgot-password" element={<ForgotPassword />} />
                  <Route path="/customer/reset-password" element={<ResetPassword />} />
                  <Route path="/customer" element={
                    <ProtectedRoute customerOnly>
                      <Navigate to="/customer/dashboard" replace />
                    </ProtectedRoute>
                  } />
                  <Route path="/customer/dashboard" element={
                    <ProtectedRoute customerOnly>
                      <CustomerDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/customer/classes" element={
                    <ProtectedRoute customerOnly>
                      <CustomerClasses />
                    </ProtectedRoute>
                  } />
                  <Route path="/customer/profile" element={
                    <ProtectedRoute customerOnly>
                      <CustomerProfile />
                    </ProtectedRoute>
                  } />
                  <Route path="/customer/passes" element={
                    <ProtectedRoute customerOnly>
                      <CustomerPasses />
                    </ProtectedRoute>
                  } />
                  <Route path="/customer/gift-cards" element={
                    <ProtectedRoute customerOnly>
                      <CustomerGiftCards />
                    </ProtectedRoute>
                  } />
                  <Route path="/customer/routes" element={
                    <ProtectedRoute customerOnly>
                      <CustomerRoutes />
                    </ProtectedRoute>
                  } />
                  <Route path="/customer/sends" element={
                    <ProtectedRoute customerOnly>
                      <CustomerSends />
                    </ProtectedRoute>
                  } />

                  {/* Embedded Member Portal Routes
                      Gyms embed with: <iframe src="https://gymname.elcapit.com/embed" />
                      Uses localStorage tokens (works in cross-origin iframes).
                      Reuses existing Customer pages — EmbedProvider switches nav to /embed/* paths. */}
                  <Route path="/embed" element={
                    <EmbedProvider>
                      <EmbedPortal />
                    </EmbedProvider>
                  } />
                  <Route path="/embed/dashboard" element={
                    <EmbedProvider>
                      <ProtectedRoute customerOnly>
                        <CustomerDashboard />
                      </ProtectedRoute>
                    </EmbedProvider>
                  } />
                  <Route path="/embed/classes" element={
                    <EmbedProvider>
                      <ProtectedRoute customerOnly>
                        <CustomerClasses />
                      </ProtectedRoute>
                    </EmbedProvider>
                  } />
                  <Route path="/embed/passes" element={
                    <EmbedProvider>
                      <ProtectedRoute customerOnly>
                        <CustomerPasses />
                      </ProtectedRoute>
                    </EmbedProvider>
                  } />
                  <Route path="/embed/gift-cards" element={
                    <EmbedProvider>
                      <ProtectedRoute customerOnly>
                        <CustomerGiftCards />
                      </ProtectedRoute>
                    </EmbedProvider>
                  } />
                  <Route path="/embed/profile" element={
                    <EmbedProvider>
                      <ProtectedRoute customerOnly>
                        <CustomerProfile />
                      </ProtectedRoute>
                    </EmbedProvider>
                  } />

                  {/* Public Storefront — no auth required */}
                  <Route path="/embed/store" element={
                    <EmbedProvider>
                      <CartProvider>
                        <Storefront />
                      </CartProvider>
                    </EmbedProvider>
                  } />
                  <Route path="/embed/store/cart" element={
                    <EmbedProvider>
                      <CartProvider>
                        <ShoppingCart />
                      </CartProvider>
                    </EmbedProvider>
                  } />
                  <Route path="/embed/store/checkout" element={
                    <EmbedProvider>
                      <CartProvider>
                        <GuestCheckout />
                      </CartProvider>
                    </EmbedProvider>
                  } />
                  <Route path="/embed/store/success" element={
                    <EmbedProvider>
                      <CheckoutSuccess />
                    </EmbedProvider>
                  } />

                  {/* Public Day Pass purchase — no auth required */}
                  <Route path="/day-pass" element={<DayPassPurchase />} />
                  <Route path="/embed/day-pass" element={
                    <EmbedProvider>
                      <DayPassPurchase />
                    </EmbedProvider>
                  } />

                  {/* Platform Admin Routes - Wrap entire section with auth provider */}
                  <Route path="/platform-admin/*" element={<PlatformAdminAuthProvider><PlatformAdminRoutes /></PlatformAdminAuthProvider>} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            <NotificationContainer />
          </AuthProvider>
        </TenantProvider>
      </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
